import React, { useEffect, useRef, useState } from "react";
import "./admin-user.css";
import { FaSearch } from "react-icons/fa";
import { HashLoader } from "react-spinners";
import { FaEye } from "react-icons/fa6";
import { FaEdit } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import ToastNotification, { showToast } from "../../components/toasts/ToastNotification";
import ConfirmModal from "../../components/modal/confirm-modal";
import { deleteUserForAdmin, getAllUsersForAdmin } from "../../api/user.api";
import { useUser } from "../../context/UserContext";
import { useNavigate } from "react-router-dom";
import { hasPermission } from "../../utils/auth";
import Pagination from "../../components/pagination/Pagination";
import { useDebounce } from "../../hooks/useDebounce";

const ITEMS_PER_PAGE = 8;

export const AdminUser = () => {
  const { user } = useUser();
  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState("");
  const debouncedSearchInput = useDebounce(searchInput, 500);
  const [sortByBalance, setSortByBalance] = useState(false);

  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedIdToDelete, setSelectedIdToDelete] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    document.title = "Admin - Người dùng";
  }, []);

  useEffect(() => {
    fetchUserList();
  }, [currentPage, debouncedSearchInput, sortByBalance]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchInput]);

  const fetchUserList = async () => {
    setLoading(true);
    try {
      const res = await getAllUsersForAdmin(
        ITEMS_PER_PAGE,
        currentPage,
        debouncedSearchInput.trim(),
        sortByBalance
      );

      if (res.success) {
        setUsers(res.data.users || []);
        setTotalPages(res.data.totalPages || 1);
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleDeleteUser = async (userId) => {
    setLoading(true);
    setIsModalOpen(false);
    try {
      const res = await deleteUserForAdmin(userId);
      if (res.success) {
        showToast("Xóa người dùng thành công", "success");
        fetchUserList();
        setSelectedIdToDelete("");
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loader-container">
        <HashLoader color="#092339" />
      </div>
    );
  }

  return (
    <div className="admin-payment-container">
      <p className="tab-nav-title">Danh sách người dùng</p>
      <div className="admin-product-header">
        <div className="search-filter-container">
          <div className="admin-search-input-container">
            <input
              type="text"
              placeholder="Tìm kiếm theo mã người dùng hoặc tên người dùng"
              className="admin-search-input"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setCurrentPage(1);
              }}
            />
            <FaSearch className="admin-search-icon" />
          </div>
          <select
            className="filter-selection"
            value={sortByBalance}
            onChange={(e) => {
              setSortByBalance(e.target.value);
            }}
          >
            <option value="" disabled>Xếp theo số tiền</option>
            <option value={true}>Có</option>
            <option value={false}>Không</option>
          </select>
        </div>
      </div>

      <div className="product-table-container">
        <table className="product-table">
          <thead>
            <tr>
              <th>STT</th>
              <th>ID</th>
              <th>Tên người dùng</th>
              <th>Vai trò</th>
              <th>Số dư</th>
              <th>Trạng thái</th>
              <th>Tạo lúc</th>
              <th>Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {users.length > 0 ? (
              users.map((tx, index) => (
                <tr key={tx._id}>
                  <td>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                  <td>{tx._id}</td>
                  <td>
                    <a
                      onClick={() =>
                        tx._id !== user._id &&
                        navigate(`/super-admin/users/view/${tx._id}`)
                      }
                      className="table-product-name"
                    >
                      {tx.username}
                    </a>
                  </td>
                  <td>
                    <div className={`user-role-container ${tx.role}`}>
                      {tx.role}
                    </div>
                  </td>
                  <td style={{ color: "#3498db" }}>
                    {tx.balance?.toLocaleString() || 0}đ
                  </td>
                  <td>
                    <div
                      className={`user-status-container ${tx.isBanned ? "user-banned" : "user-active"
                        }`}
                    >
                      {tx.isBanned ? "Bị chặn" : "Hoạt động"}
                    </div>
                  </td>
                  <td>{new Date(tx.createdAt).toLocaleString()}</td>
                  <td className="action-cell">
                    <div className="action-buttons-container">
                      {tx._id !== user._id && (
                        <>
                          <button
                            onClick={() =>
                              navigate(`/super-admin/users/view/${tx._id}`)
                            }
                            className="view-btn action-button"
                          >
                            <FaEye className="action-icon" />
                          </button>
                          {hasPermission(user?.role, "edit:user") && (
                            <button
                              onClick={() =>
                                navigate(`/super-admin/users/edit/${tx._id}`)
                              }
                              className="edit-btn action-button"
                            >
                              <FaEdit className="action-icon" />
                            </button>
                          )}
                          {hasPermission(user?.role, "delete:user") && (
                            <button
                              onClick={() => {
                                setIsModalOpen(true);
                                setSelectedIdToDelete(tx._id);
                              }}
                              className="delete-btn action-button"
                            >
                              <MdDelete className="action-icon" />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8">Không có người dùng</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPrev={handlePrevPage}
          onNext={handleNextPage}
        />
      )}

      <ToastNotification />

      <ConfirmModal
        isOpen={isModalOpen}
        onConfirm={() => handleDeleteUser(selectedIdToDelete)}
        onClose={() => setIsModalOpen(false)}
        message={
          "Hãy chắc chắn rằng bạn đang xóa một người dùng, điều này sẽ xóa tất cả những dữ liệu về người dùng này."
        }
        title={"Xóa người dùng"}
      />
    </div>
  );
};
